import {
  useState,
  useMemo,
  useEffect,
  useContext,
  FocusEventHandler,
  FocusEvent,
  useCallback,
  ChangeEvent,
  ReactNode,
} from 'react';
import { darkModeAware } from '@glu/theming';
import { withInputAPI, FormContext } from '@glu/validation-react';
import { AccessibilityText } from '@glu/utilities-react';
import { MinusIcon, PlusIcon } from '@glu/icons-react';
import classnames from 'classnames';
import locale from '@glu/locale';
import BaseInput, { IBaseInputProps } from '../Input/BaseInput';
import Error, { IErrorProps } from '../Error/Error';
import useStyles from './NumberInput.styles';
import LabelText, { ILabelTextProps } from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import HelpText from '../HelpText/HelpText';

let uniqueId = 0;

export interface INumberInputProps
  extends Pick<IErrorProps, 'error'>,
    Pick<ILabelTextProps, 'optional' | 'screenReaderLabel' | 'inline' | 'tooltipContent'>,
    Omit<IBaseInputProps, 'id' | 'min' | 'max' | 'onChange' | 'step'> {
  /** Array of all current errors */
  errors?: IErrorProps['error'][];
  /** Help text to display with input */
  helpText?: ReactNode;
  /** Id applied to the input. If not provided id will be `number-input-${uniqueId++}` */
  htmlId?: string;
  /** Custom classes applied to input element */
  inputClass?: string;
  labelSpanClass?: ILabelTextProps['className'];
  labelText: ILabelTextProps['label'];
  /** Number value that the input cannot exceed */
  max?: number;
  /** Number value that the input cannot go below */
  min?: number;
  /** Name HTML attribute */
  name: string;
  /** The blur event handler for the input and plus/minus buttons */
  onBlur: FocusEventHandler;
  /** The change event handler for the input and plus/minus buttons */
  onChange?: (name: string, value: string | number, fromBlur?: boolean) => void;
  /** Value to increment with, can by a number or string 'any' */
  step?: number | string;
  /** Number value of module */
  value?: number | string;
}

const NumberInput = ({
  className,
  dark,
  disabled,
  error,
  errors,
  helpText,
  htmlId,
  inline,
  inputClass,
  labelSpanClass,
  labelText,
  max,
  min,
  onBlur,
  onChange,
  optional,
  screenReaderLabel,
  step = 1,
  tooltipContent,
  value,
  warning,
  ...props
}: INumberInputProps) => {
  const [internalError, setInternalError] = useState<string>();
  const errorMessage = internalError || error || errors?.[0];
  const classes = useStyles();
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `number-input-${uniqueId++}`, [htmlId]);
  const stringValue = useMemo(
    () => (typeof value === 'number' ? value.toString() : value),
    [value]
  );
  const errorId = useMemo(() => `error-${id}`, [id]);
  const aboveOrEqualMin = useCallback(
    (val: INumberInputProps['value']) => {
      const numberVal = parseFloat(`${val}`);
      return (val || val === 0) && (min || min === 0) ? min <= numberVal : true;
    },
    [min]
  );
  const belowOrEqualMax = useCallback(
    (val: INumberInputProps['value']) => {
      const numberVal = parseFloat(`${val}`);
      return val && (max || max === 0) ? max >= numberVal : true;
    },
    [max]
  );
  const getStep = useMemo(() => (typeof step === 'number' ? step : 1), [step]);

  const getValue = useMemo(() => {
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    return numberValue || min || 0;
  }, [min, value]);

  const updateValue = useCallback(
    (val: INumberInputProps['value']) => {
      let newValue: INumberInputProps['value'] = typeof val === 'string' ? parseFloat(val) : val;
      if (Number.isNaN(newValue)) {
        // Replace isNaN values with empty string for form validation
        // Fixes warning: `value` prop on `input` should not be null.
        newValue = '';
      }
      onChange?.(props.name, newValue!);
    },
    [onChange, props.name]
  );

  const formValue = useContext(FormContext);

  const validate = useCallback(() => {
    const internalErrors = [];
    if (min && !max && !aboveOrEqualMin(value)) {
      internalErrors.push(locale.get('numberAboveMin', min.toString()));
    }
    if (!min && max && !belowOrEqualMax(value)) {
      internalErrors.push(locale.get('numberBelowMax', max.toString()));
    }
    if (min && max && (!aboveOrEqualMin(value) || !belowOrEqualMax(value))) {
      internalErrors.push(locale.get('numberBetween', min.toString(), max.toString()));
    }
    setInternalError(internalErrors.length > 0 ? internalErrors[0] : undefined);
    return {
      data: value,
      errors: internalErrors,
      isValid: internalErrors.length === 0,
    };
  }, [aboveOrEqualMin, belowOrEqualMax, max, min, value]);

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    onBlur?.(e);
    validate();
  };

  const handleMinusClick = () => {
    const curValue = getValue;
    const newValue = (curValue - getStep).toPrecision(10);
    updateValue(min && curValue - getStep < min ? min : newValue);
  };

  useEffect(
    () => formValue?.registerSelfValidatingChild?.(props.name, validate),
    [formValue, props.name, validate]
  );

  return (
    <label
      className={classnames(classes.root, { [classes.inline]: inline }, className)}
      htmlFor={id}
    >
      <LabelText
        className={labelSpanClass}
        hasError={!!errorMessage}
        inline={inline}
        label={labelText}
        optional={optional}
        screenReaderLabel={screenReaderLabel}
        tooltipContent={tooltipContent}
        warning={warning}
      />
      <div className={classnames({ [classes.inlineNumber]: inline })}>
        <div className={classes.number}>
          <button
            className={classnames(
              classes.btnCtrl,
              classes.btnMinus,
              { [classes.error]: !!errorMessage },
              { [classes.dark]: dark },
              { [classes.warning]: !!warning },
              className
            )}
            data-qa="btnDown"
            disabled={disabled || !!(min && value && min >= parseFloat(`${value}`))}
            onBlur={handleBlur}
            onClick={handleMinusClick}
            type="button"
          >
            <MinusIcon />
            <AccessibilityText>{locale.get('decrement')}</AccessibilityText>
          </button>
          <BaseInput
            id={id}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            aria-errormessage={errorMessage ? errorId : undefined}
            aria-invalid={!!errorMessage}
            className={inputClass}
            dark={dark}
            disabled={!!disabled}
            error={errorMessage}
            max={max?.toString() || ''}
            min={min?.toString() || ''}
            onBlur={handleBlur}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e?.target?.value)}
            step={typeof step === 'string' ? step : step.toString()}
            type="number"
            value={stringValue || ''}
            warning={warning}
          />
          <button
            className={classnames(
              classes.btnCtrl,
              classes.btnPlus,
              { [classes.error]: !!errorMessage },
              { [classes.dark]: dark },
              { [classes.warning]: !!warning },
              className
            )}
            data-qa="btnUp"
            disabled={disabled || !!(max && value && max <= parseFloat(`${value}`))}
            onBlur={handleBlur}
            onClick={() => updateValue((getValue + getStep).toPrecision(10))}
            type="button"
          >
            <PlusIcon />
            <AccessibilityText>{locale.get('increment')}</AccessibilityText>
          </button>
        </div>
        <Error dark={dark} error={errorMessage} htmlId={errorId} warning={warning} />
        {helpText && <HelpText>{helpText}</HelpText>}
      </div>
      {optional && inline ? (
        <OptionalInlineText hasError={!!errorMessage} warning={warning} />
      ) : null}
    </label>
  );
};

const StyledNumber = darkModeAware(NumberInput);
const NumberWithInputApi = withInputAPI(StyledNumber);
export default NumberWithInputApi;
export const ValidatedNumberInput = NumberWithInputApi;
export const BaseNumberInput = NumberInput;
